import { CustomAttributeType } from "electrodb";
import { TenantId, SiteId, AssetId } from "@visioni/a3s-schema";

/**
 * ElectroDB attribute primitives for branded ids and ISO dates, shared across
 * the feature's entities (the Reports table in `reports-db/` and the
 * subscriptions table in `subscriptions-db/`). Hoisted to `fleet-services/` so
 * both entities source the same primitives rather than copying them.
 *
 * These mirror the module-local primitives in
 * `features/a3s-media/src/fleet-services/media-db/schema.ts` (lines 7–35).
 * They are copied locally — rather than imported from a3s-media — because
 * a3s-reports must not depend on a3s-media's internals (design §4.2). The
 * `set`/`get` hooks keep the on-table value a plain string while exposing the
 * branded type to callers. Once a third feature needs these, hoist them into a
 * shared package (e.g. packages/a3s-codecs).
 */

export const tenantIdAttribute = {
  type: CustomAttributeType<TenantId>("string"),
  required: true as const,
  set: (value?: TenantId) => (value ? TenantId.toString(value) : undefined),
  get: (value?: string) => (value ? TenantId.fromString(value) : undefined),
};

export const siteIdAttribute = {
  type: CustomAttributeType<SiteId>("string"),
  required: true as const,
  set: (value?: SiteId) => (value ? SiteId.toString(value) : undefined),
  get: (value?: string) => (value ? SiteId.fromString(value) : undefined),
};

export const assetIdAttribute = {
  type: CustomAttributeType<AssetId>("string"),
  required: true as const,
  set: (value?: AssetId) => (value ? AssetId.toString(value) : undefined),
  get: (value?: string) => (value ? AssetId.fromString(value) : undefined),
};

/**
 * Calendar-date attribute (`YYYY-MM-DD`, e.g. a report `periodStart` or a
 * subscription `startDate`). Stored and exposed as a plain string — these are
 * date-only values, deliberately NOT the `ISODateString` brand from
 * `@visioni/a3s-codecs`, which is a full ISO *datetime*. The wire contracts that
 * feed these (`ReportJob`'s `z.iso.date()` fields) already produce plain
 * date strings, so no brand bridging is needed at the entity boundary.
 */
export const dateAttribute = {
  type: "string",
  required: true as const,
} as const;
